import React, { useState, useEffect, useCallback } from 'react';
import { Device } from "./DeviceManagementPage";
import { useOri } from '../../context/OriContext';
import {
    FormControl, InputLabel, MenuItem, Select, SelectChangeEvent,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Typography, FormControlLabel, Switch, Divider
} from '@mui/material';
import { getEnvironmentDAI } from "../../App";

interface AddDeviceFormModalProps {
    device?: Device;
    onClose: () => void;
    onSave: (device: {
        deviceName: string;
        ori?: string;
        deviceId?: string;
        priority?: number;
        collectFace?: boolean;
        collectTwoFinger?: boolean;
    }) => Promise<boolean>;
}

const AddDeviceFormModal: React.FC<AddDeviceFormModalProps> = ({ device, onClose, onSave }) => {
    const { availableOris, selectedOri } = useOri();
    const [dai, setDai] = useState('');

    const [formData, setFormData] = useState({
        deviceName: '',
        priority: 3,
        collectFace: false,
        collectTwoFinger: false,
    });

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (device) {
            setFormData({
                deviceName: device.deviceName || '',
                priority: device.priority || 3,
                collectFace: device.collectFace ?? false,
                collectTwoFinger: device.collectTwoFinger ?? false,
            });
        } else {
            setFormData({
                deviceName: '',
                priority: 3,
                collectFace: false,
                collectTwoFinger: false,
            });
        }
    }, [device]);

    useEffect(() => {
        try {
            setDai(getEnvironmentDAI());
        } catch (e) {
            console.error("Failed to fetch DAI", e);
        }
    }, []);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleSelectChange = useCallback((e: SelectChangeEvent) => {
        const { name, value } = e.target;
        if (name === 'priority') {
            setFormData(prev => ({ ...prev, priority: parseInt(value, 10) }));
        }
    }, []);

    const handleSwitchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: checked }));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);
        try {
            const payload = {
                ...(device && { deviceId: device.deviceId }),
                deviceName: formData.deviceName,
                priority: formData.priority,
                collectFace: formData.collectFace,
                collectTwoFinger: formData.collectTwoFinger,
                // ORI comes from the selected context, not a form field
                ori: device?.ori || selectedOri || undefined,
            };
            const success = await onSave(payload);
            if (success) {
                onClose();
            } else {
                setError('Could not save the device. Please try again.');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setIsSaving(false);
        }
    };

    // Display the ORI that will be assigned (read-only)
    const displayOri = device?.ori || selectedOri || '';
    const oriLabel = availableOris.find(a => a.ori === displayOri)
        ? `${availableOris.find(a => a.ori === displayOri)!.name} (${displayOri})`
        : displayOri;

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{device ? 'Edit Device' : 'Add New Device'}</DialogTitle>
            <form onSubmit={handleSubmit} id="device-form">
                <DialogContent>
                    {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

                    <TextField
                        autoFocus
                        margin="dense"
                        name="deviceName"
                        label="Device Name"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={formData.deviceName}
                        onChange={handleChange}
                        required
                    />

                    {/* ORI — display only, set by system */}
                    <TextField
                        margin="dense"
                        label="Device ORI"
                        value={oriLabel}
                        variant="standard"
                        fullWidth
                        disabled
                        sx={{ mt: 1 }}
                    />

                    <FormControl fullWidth variant="standard" sx={{ mt: 2 }}>
                        <InputLabel id="device-priority-select-label">Priority</InputLabel>
                        <Select
                            labelId="device-priority-select-label"
                            name="priority"
                            value={String(formData.priority)}
                            onChange={handleSelectChange}
                            required
                        >
                            {[1, 2, 3, 4].map(p => (
                                <MenuItem key={p} value={p}>{p}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* DAI — display only, set by environment */}
                    <TextField
                        margin="dense"
                        name="dai"
                        label="Destination Agency (DAI)"
                        value={dai}
                        variant="standard"
                        fullWidth
                        disabled
                        sx={{ mt: 1 }}
                    />

                    <Divider sx={{ mt: 3, mb: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Capture Settings
                    </Typography>

                    <FormControlLabel
                        control={
                            <Switch
                                name="collectFace"
                                checked={formData.collectFace}
                                onChange={handleSwitchChange}
                            />
                        }
                        label="Collect Face Photo"
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                name="collectTwoFinger"
                                checked={formData.collectTwoFinger}
                                onChange={handleSwitchChange}
                            />
                        }
                        label="Collect Two-Finger Slaps"
                        sx={{ display: 'block', mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                    <Button type="submit" form="device-form" disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddDeviceFormModal;
